import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './TooltipTour.module.css';

/* ─────────────────────────── Types ─────────────────────────── */

export interface TourStep {
  /** CSS selector of the element this step points to */
  target: string;
  /** Optional bold title shown above the content */
  title?: string;
  /** Main description text */
  content: string;
}

export interface TooltipTourProps {
  steps: TourStep[];
  /**
   * Key used to persist tour completion in localStorage.
   * Use a version suffix (e.g. "my-tour-v1") to reset the tour after changes.
   */
  tourKey: string;
  /**
   * When true the localStorage check is skipped and the tour always runs.
   * Useful for development — set via an env variable:
   *   disablePersistence={process.env.NODE_ENV === 'development'}
   */
  disablePersistence?: boolean;
  onComplete?: () => void;
  onSkip?: () => void;
}

/* ─────────────────────────── Positioning ─────────────────────────── */

type ArrowSide = 'top' | 'bottom' | 'left' | 'right';

interface Position {
  top: number;
  left: number;
  arrowSide: ArrowSide;
  /** px offset of the arrow along its axis so it still points at the target center */
  arrowOffset: number;
}

const TOOLTIP_WIDTH  = 288;
const TOOLTIP_OFFSET = 14; // gap between target edge and tooltip
const ARROW_SIZE     = 7;  // half-width of the CSS arrow
const EDGE_MARGIN    = 10; // minimum distance from viewport edge

function clamp(val: number, min: number, max: number) {
  return Math.max(min, Math.min(max, val));
}

function computePosition(
  targetRect: DOMRect,
  tooltipRect: DOMRect,
): Position {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const tw = tooltipRect.width  || TOOLTIP_WIDTH;
  const th = tooltipRect.height || 120;

  const space = {
    bottom: vh - targetRect.bottom,
    top:    targetRect.top,
    right:  vw - targetRect.right,
    left:   targetRect.left,
  };

  const side = (Object.entries(space) as [ArrowSide, number][])
    .sort(([, a], [, b]) => b - a)[0][0];

  const cx = targetRect.left + targetRect.width  / 2; // target center X
  const cy = targetRect.top  + targetRect.height / 2; // target center Y

  let top = 0;
  let left = 0;
  let arrowSide: ArrowSide;
  let arrowOffset = 0;

  switch (side) {
    case 'bottom': {
      top  = targetRect.bottom + TOOLTIP_OFFSET;
      const idealLeft = cx - tw / 2;
      left = clamp(idealLeft, EDGE_MARGIN, vw - tw - EDGE_MARGIN);
      arrowSide   = 'top';
      arrowOffset = clamp(cx - left - ARROW_SIZE, ARROW_SIZE * 2, tw - ARROW_SIZE * 4);
      break;
    }
    case 'top': {
      top  = targetRect.top - th - TOOLTIP_OFFSET;
      const idealLeft = cx - tw / 2;
      left = clamp(idealLeft, EDGE_MARGIN, vw - tw - EDGE_MARGIN);
      arrowSide   = 'bottom';
      arrowOffset = clamp(cx - left - ARROW_SIZE, ARROW_SIZE * 2, tw - ARROW_SIZE * 4);
      break;
    }
    case 'right': {
      left = targetRect.right + TOOLTIP_OFFSET;
      const idealTop = cy - th / 2;
      top  = clamp(idealTop, EDGE_MARGIN, vh - th - EDGE_MARGIN);
      arrowSide   = 'left';
      arrowOffset = clamp(cy - top - ARROW_SIZE, ARROW_SIZE * 2, th - ARROW_SIZE * 4);
      break;
    }
    case 'left': {
      left = targetRect.left - tw - TOOLTIP_OFFSET;
      const idealTop = cy - th / 2;
      top  = clamp(idealTop, EDGE_MARGIN, vh - th - EDGE_MARGIN);
      arrowSide   = 'right';
      arrowOffset = clamp(cy - top - ARROW_SIZE, ARROW_SIZE * 2, th - ARROW_SIZE * 4);
      break;
    }
  }

  return { top, left, arrowSide, arrowOffset };
}

/* ─────────────────────────── Component ─────────────────────────── */

const STORAGE_PREFIX = 'ds-tour-completed:';

export const TooltipTour = React.memo<TooltipTourProps>(({
  steps,
  tourKey,
  disablePersistence = false,
  onComplete,
  onSkip,
}) => {
  const storageKey = `${STORAGE_PREFIX}${tourKey}`;

  const isCompleted = !disablePersistence && localStorage.getItem(storageKey) === 'true';

  const [active,   setActive]   = useState(!isCompleted && steps.length > 0);
  const [index,    setIndex]    = useState(0);
  const [pos,      setPos]      = useState<Position | null>(null);
  const [visible,  setVisible]  = useState(false);
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const tooltipRef = useRef<HTMLDivElement>(null);
  const lastIndexRef = useRef<number>(index);

  const markComplete = useCallback(() => {
    if (!disablePersistence && dontShowAgain) {
      localStorage.setItem(storageKey, 'true');
    }
  }, [disablePersistence, storageKey, dontShowAgain]);

  const handleSkip = useCallback(() => {
    markComplete();
    setActive(false);
    onSkip?.();
  }, [markComplete, onSkip]);

  const handleFinish = useCallback(() => {
    markComplete();
    setActive(false);
    onComplete?.();
  }, [markComplete, onComplete]);

  const handleNext = useCallback(() => {
    if (index < steps.length - 1) setIndex(i => i + 1);
    else handleFinish();
  }, [index, steps.length, handleFinish]);

  const handlePrev = useCallback(() => {
    if (index > 0) setIndex(i => i - 1);
  }, [index]);

  /* Keyboard navigation */
  useEffect(() => {
    if (!active) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape')       handleSkip();
      if (e.key === 'ArrowRight')   handleNext();
      if (e.key === 'ArrowLeft')    handlePrev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, handleSkip, handleNext, handlePrev]);

  /* Reposition whenever the step changes */
  const reposition = useCallback(() => {
    if (!active) return;
    const step = steps[index];
    if (!step) return;

    const target = document.querySelector(step.target);
    if (!target) return;

    target.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

    const targetRect  = target.getBoundingClientRect();
    const tooltipRect = tooltipRef.current?.getBoundingClientRect() ?? new DOMRect();

    setPos(computePosition(targetRect, tooltipRect));
    setVisible(true);
  }, [active, index, steps]);

  /* Run after paint so the tooltip dimensions are known */
  useLayoutEffect(() => {
    if (lastIndexRef.current !== index) {
      setVisible(false);
      lastIndexRef.current = index;
    }
    const id = requestAnimationFrame(reposition);
    return () => cancelAnimationFrame(id);
  }, [reposition, index]);

  /* Re-calculate on resize or scroll */
  useEffect(() => {
    if (!active) return;
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    return () => {
      window.removeEventListener('resize', reposition);
      window.removeEventListener('scroll', reposition, true);
    };
  }, [active, reposition]);

  if (!active || !steps[index]) return null;

  const step     = steps[index];
  const isFirst  = index === 0;
  const isLast   = index === steps.length - 1;

  const tooltipStyle: React.CSSProperties = pos
    ? { top: pos.top, left: pos.left, visibility: visible ? 'visible' : 'hidden' }
    : { visibility: 'hidden' };

  /* Arrow inline style — offset along the axis so it tracks the target */
  const arrowStyle: React.CSSProperties = {};
  if (pos) {
    const isHorizontalArrow = pos.arrowSide === 'top' || pos.arrowSide === 'bottom';
    if (isHorizontalArrow) arrowStyle.left = pos.arrowOffset;
    else                   arrowStyle.top  = pos.arrowOffset;
  }

  return createPortal(
    <div
      ref={tooltipRef}
      className={[
        styles.tooltip,
        pos ? styles[`arrow-${pos.arrowSide}`] : '',
      ].filter(Boolean).join(' ')}
      style={tooltipStyle}
      role="dialog"
      aria-modal="false"
      aria-label={step.title ?? 'Tour'}
    >
      {/* Arrow */}
      <span className={styles.arrow} style={arrowStyle} aria-hidden />

      {/* Header */}
      <div className={styles.header}>
        {step.title && <span className={styles.title}>{step.title}</span>}
        <button
          className={styles.skipButton}
          onClick={handleSkip}
          aria-label="Skip tour"
          title="Skip tour"
        >
          ✕
        </button>
      </div>

      {/* Content */}
      <p className={styles.content}>{step.content}</p>

      {/* Footer */}
      <div className={styles.footer}>
        <span className={styles.counter}>
          {index + 1} / {steps.length}
        </span>
        <div className={styles.actions}>
          {!isFirst && (
            <button className={styles.btnSecondary} onClick={handlePrev}>
              Back
            </button>
          )}
          <button className={styles.btnPrimary} onClick={handleNext}>
            {isLast ? 'Finish' : 'Next'}
          </button>
        </div>
      </div>

      {!disablePersistence && (
        <label className={styles.dontShow}>
          <input
            type="checkbox"
            checked={dontShowAgain}
            onChange={e => setDontShowAgain(e.target.checked)}
          />
          Don&apos;t show this again
        </label>
      )}
    </div>,
    document.body,
  );
});

TooltipTour.displayName = 'TooltipTour';
